// src/cockpit4/components/OntologyPanel.tsx
// ============================================
// Ontology「企业地图」面板 — P0① 差距补齐
//
// 对标李开复「地图（Ontology）：企业所知道的一切，让 AI 不靠猜」。
// 定位：把 Vault 里的半结构化 Markdown 固化为「实体-关系」本体索引，
//      作为 Hermes RAG/规则引擎优先注入的地图上下文，从根上降幻觉。
//
// 载体：Vault Markdown 索引（贴合「Obsidian 唯一大脑」，经 /vault-api 读写）。
// 目录：09_Knowledge（知识中心）/Ontology/
//   6 类实体：客户 / 项目 / 承诺 / 信源 / 内容资产 / 供应商
//   frontmatter: type: ontology
//   ## 实体   列表（名称 | 描述 | 关键字段）
//   ## 关系   双向关系（客户↔项目、客户↔承诺、信源↔内容资产、项目↔供应商）
// ============================================

import { useEffect, useCallback, useState } from "react";
import { motion } from "framer-motion";
import {
  Map, Plus, RefreshCw, Loader2, Save, BookOpen, Link2, UsersRound,
  FolderKanban, UserRoundCheck, Radio, FileStack, Truck, Trash2,
  GitBranch, List,
} from "lucide-react";

// ============================================
// 6 类实体定义
// ============================================
const ONTO_DIR = "09_Knowledge（知识中心）/Ontology";

const ONTO_TYPES = [
  { id: "customer", label: "客户", icon: UsersRound, color: "#38BDF8", file: "客户-index.md" },
  { id: "project", label: "项目", icon: FolderKanban, color: "#A78BFA", file: "项目-index.md" },
  { id: "commitment", label: "承诺", icon: UserRoundCheck, color: "#FB7185", file: "承诺-index.md" },
  { id: "source", label: "信源", icon: Radio, color: "#34D399", file: "信源-index.md" },
  { id: "asset", label: "内容资产", icon: FileStack, color: "#F59E0B", file: "内容资产-index.md" },
  { id: "supplier", label: "供应商", icon: Truck, color: "#22D3EE", file: "供应商-index.md" },
] as const;

interface OntoEntity {
  name: string;
  desc: string;
  fields: string; // 关键字段，逗号分隔
}

/** 解析索引 md → 实体列表 + 关系文本 */
function parseIndex(md: string): { entities: OntoEntity[]; relations: string[] } {
  const entities: OntoEntity[] = [];
  const relations: string[] = [];
  let inEntity = false;
  let inRel = false;
  for (const line of md.split("\n")) {
    const t = line.trim();
    if (t.startsWith("## 实体")) { inEntity = true; inRel = false; continue; }
    if (t.startsWith("## 关系")) { inRel = true; inEntity = false; continue; }
    if (t.startsWith("#") || t.startsWith("---") || t.includes("type: ")) continue;
    if (inEntity && t.startsWith("|") && !t.startsWith("| 名称")) {
      // 跳过 Markdown 表格分隔行（`| --- | --- |`），否则会被当成名称为 "---" 的实体
      if (/^[|\-\s:]+$/.test(t)) continue;
      const c = t.split("|").map((s) => s.trim());
      const name = c[1]?.trim();
      if (name) entities.push({ name, desc: c[2]?.trim() || "", fields: c[3]?.trim() || "" });
    }
    if (inRel && t.startsWith("-")) {
      relations.push(t.replace(/^-/, "").trim());
    }
  }
  return { entities, relations };
}

function toIndexMd(fileTitle: string, entities: OntoEntity[], relations: string[]): string {
  const head =
    `---\ntype: ontology\ntypeOf: ${fileTitle.replace("-index.md", "")}\nupdated: ${new Date().toISOString().slice(0, 10)}\n---\n\n# ${fileTitle.replace("-index.md", "")} · 本体索引\n\n> 企业地图的一部分。Hermes 检索时优先注入本索引，作为「先看地图再找碎片」的锚点。\n\n## 实体\n\n| 名称 | 描述 | 关键字段 |\n| --- | --- | --- |\n`;
  const body = entities
    .map((e) => `| **${e.name}** | ${e.desc} | ${e.fields} |`)
    .join("\n");
  const rel =
    (relations.length ? "\n## 关系\n\n" + relations.map((r) => `- ${r}`).join("\n") : "\n## 关系\n\n- （待补充）") + "\n";
  return head + body + rel;
}

async function readMd(p: string): Promise<string> {
  try {
    const r = await fetch(`/vault-api/read?path=${encodeURIComponent(p)}`);
    if (!r.ok) return "";
    const j = await r.json();
    return j?.success && typeof j.content === "string" ? j.content : "";
  } catch { return ""; }
}

async function writeMd(p: string, c: string): Promise<boolean> {
  try {
    const r = await fetch(`/vault-api/write?path=${encodeURIComponent(p)}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ content: c }),
    });
    const j = await r.json();
    return !!j?.success;
  } catch { return false; }
}

export default function OntologyPanel() {
  const [activeType, setActiveType] = useState<string>("customer");
  const [view, setView] = useState<"editor" | "graph">("editor");
  const [docs, setDocs] = useState<Record<string, { entities: OntoEntity[]; relations: string[] }>>({});
  const [loading, setLoading] = useState(true);

  const loadAll = useCallback(async () => {
    setLoading(true);
    const map: Record<string, { entities: OntoEntity[]; relations: string[] }> = {};
    for (const t of ONTO_TYPES as any) {
      const raw = await readMd(`${ONTO_DIR}/${t.file}`);
      map[t.id] = parseIndex(raw);
    }
    setDocs(map);
    setLoading(false);
  }, []);

  useEffect(() => { void loadAll(); }, [loadAll]);

  const active = ONTO_TYPES.find((t) => t.id === activeType) as any;
  const cur = docs[activeType] || { entities: [], relations: [] };

  const saveType = async (id: string, entities: OntoEntity[], relations: string[]) => {
    const t = ONTO_TYPES.find((x) => x.id === id) as any;
    const ok = await writeMd(`${ONTO_DIR}/${t.file}`, toIndexMd(t.file, entities, relations));
    if (ok) setDocs((d) => ({ ...d, [id]: { entities, relations } }));
    return ok;
  };

  return (
    <div className="flex flex-col gap-3">
      {/* 标题 */}
      <div className="flex items-center gap-2 rounded-xl border border-white/[0.06] bg-white/[0.02] px-3 py-2.5">
        <Map size={14} className="text-cyan-400" />
        <span className="text-[13px] font-semibold text-white tracking-tight">Ontology · 企业地图</span>
        <span className="text-[9px] font-mono text-gray-600 uppercase tracking-wider">先看地图，再找碎片 — 降幻觉根因</span>
        <div className="flex-1" />
        <div className="flex items-center gap-0.5 rounded-md border border-white/[0.08] bg-black/20 p-0.5">
          <button onClick={() => setView("graph")} disabled={loading}
            className={`flex items-center gap-1 px-2 py-1 rounded text-[10px] font-mono transition-colors ${view === "graph" ? "bg-cyan-500/20 text-cyan-200" : "text-gray-500 hover:text-white"}`}>
            <GitBranch size={10} /> 图谱
          </button>
          <button onClick={() => setView("editor")}
            className={`flex items-center gap-1 px-2 py-1 rounded text-[10px] font-mono transition-colors ${view === "editor" ? "bg-cyan-500/20 text-cyan-200" : "text-gray-500 hover:text-white"}`}>
            <List size={10} /> 列表
          </button>
        </div>
        <button onClick={() => void loadAll()} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-white/[0.08] bg-white/[0.02] text-[10px] font-mono text-gray-400 hover:text-white">
          <RefreshCw size={11} className={loading ? "animate-spin" : ""} /> 刷新
        </button>
      </div>

      {view === "graph" ? (
        <GraphView
          docs={docs}
          loading={loading}
          onJump={(id) => { setActiveType(id); setView("editor"); }}
        />
      ) : (
        <>
      {/* 类型切换 */}
      <div className="flex items-center gap-1.5 flex-wrap">
        {ONTO_TYPES.map((t) => {
          const I = t.icon;
          const act = t.id === activeType;
          return (
            <button key={t.id} onClick={() => setActiveType(t.id)}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[10px] font-mono uppercase tracking-wider transition-all ${
                act ? "text-white bg-white/[0.06] border border-white/[0.12]" : "text-gray-500 hover:bg-white/[0.03] border border-transparent"
              }`}>
              <I size={11} style={{ color: act ? t.color : undefined }} /> {t.label}
            </button>
          );
        })}
      </div>

      <motion.div layout className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-3">
        {loading ? (
          <div className="py-16 text-center text-[11px] font-mono text-gray-600 flex items-center justify-center gap-2">
            <Loader2 size={12} className="animate-spin" /> 加载本体索引…
          </div>
        ) : (
          <Editor
            typeLabel={active?.label}
            color={active?.color}
            entities={cur.entities}
            relations={cur.relations}
            onSave={(e, r) => void saveType(active.id, e, r)}
          />
        )}
      </motion.div>
        </>
      )}
    </div>
  );
}

function Editor({
  typeLabel, color, entities, relations, onSave,
}: {
  typeLabel: string;
  color: string;
  entities: OntoEntity[];
  relations: string[];
  onSave: (e: OntoEntity[], r: string[]) => void;
}) {
  const [list, setList] = useState<OntoEntity[]>(entities);
  const [rels, setRels] = useState<string[]>(relations);
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [fields, setFields] = useState("");
  const [relInput, setRelInput] = useState("");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  // 外部刷新/切类时同步
  useEffect(() => {
    setList(entities);
    setRels(relations);
    setDirty(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entities, relations]);

  const addEntity = () => {
    if (!name.trim()) return;
    setList((p) => [...p, { name: name.trim(), desc: desc.trim(), fields: fields.trim() }]);
    setName(""); setDesc(""); setFields("");
    setDirty(true);
  };
  const addRel = () => {
    if (!relInput.trim()) return;
    setRels((p) => [...p, relInput.trim()]);
    setRelInput(""); setDirty(true);
  };
  const save = async () => { setSaving(true); await onSave(list, rels); setDirty(false); setSaving(false); };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <div className="w-2 h-2 rounded-full" style={{ background: color }} />
        <span className="text-[11px] font-medium text-gray-200">{typeLabel} · 索引</span>
        <span className="text-[9px] font-mono text-gray-600">{entities.length} 实体</span>
        <div className="flex-1" />
        <button onClick={() => void save()} disabled={saving || !dirty}
          className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[10px] font-mono transition-colors ${
            dirty ? "bg-cyan-500/20 text-cyan-200 hover:bg-cyan-500/30" : "bg-white/[0.03] text-gray-600 cursor-not-allowed"
          }`}>
          {saving ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />} 保存（写回 Vault）
        </button>
      </div>

      {/* 新增实体 */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 items-center">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="实体名称" className="px-2 py-1.5 rounded-md border border-white/[0.08] bg-black/20 text-[11px] text-white outline-none focus:border-cyan-500/40" />
        <input value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="描述" className="px-2 py-1.5 rounded-md border border-white/[0.08] bg-black/20 text-[11px] text-white outline-none focus:border-cyan-500/40" />
        <input value={fields} onChange={(e) => setFields(e.target.value)} placeholder="关键字段（,分隔）" className="px-2 py-1.5 rounded-md border border-white/[0.08] bg-black/20 text-[11px] text-white outline-none focus:border-cyan-500/40" />
      </div>
      <button onClick={addEntity} className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white/[0.05] text-gray-300 text-[11px] font-mono hover:bg-white/[0.1]"><Plus size={11} /> 添加实体</button>

      {/* 实体列表 */}
      <div>
        <div className="text-[9px] font-mono text-gray-500 uppercase tracking-wider mb-1">实体清单</div>
        {list.length === 0 && <div className="text-[10px] font-mono text-gray-600">暂无实体 — 添加本条企业核心知识</div>}
        <div className="space-y-1">
          {list.map((e, i) => (
            <div key={i} className="flex items-center gap-2 rounded-md border border-white/[0.05] bg-white/[0.01] px-2.5 py-1.5">
              <span className="text-[11px] font-medium text-gray-200 w-28 truncate">{e.name}</span>
              <span className="flex-1 text-[10px] text-gray-400 truncate">{e.desc}</span>
              <span className="text-[9px] font-mono text-gray-600 truncate max-w-[140px]">{e.fields}</span>
              <button onClick={() => { setList((p) => p.filter((_, x) => x !== i)); setDirty(true); }} className="text-gray-600 hover:text-rose-400"><Trash2 size={11} /></button>
            </div>
          ))}
        </div>
      </div>

      {/* 关系 */}
      <div>
        <div className="flex items-center gap-2 text-[9px] font-mono text-gray-500 uppercase tracking-wider mb-1"><Link2 size={10} /> 关系（wikilink 表达，如 [[客户/某某]] ↔ [[项目/酒店PMS]]）</div>
        <div className="flex gap-2 items-center">
          <input value={relInput} onChange={(e) => setRelInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addRel()} placeholder="如：[[客户/X]][[项目/Y]]" className="flex-1 px-2 py-1.5 rounded-md border border-white/[0.08] bg-black/20 text-[11px] text-white outline-none focus:border-cyan-500/40" />
          <button onClick={addRel} className="px-2.5 py-1.5 rounded-md bg-white/[0.05] text-gray-300 text-[10px] font-mono hover:bg-white/[0.1]">+ 关系</button>
        </div>
        <div className="mt-1 space-y-1">
          {rels.map((r, i) => (
            <div key={i} className="flex items-center gap-2 text-[10px] font-mono text-cyan-300/70">
              <Link2 size={9} /> <span className="flex-1">{r}</span>
              <button onClick={() => { setRels((p) => p.filter((_, x) => x !== i)); setDirty(true); }} className="text-gray-600 hover:text-rose-400"><Trash2 size={10} /></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ============================================
// 图谱视图 — 让「地图」名副其实
// ============================================
/** 关系文本里命中类型关键词 → 得到该关系连接到的类型 set */
const TYPE_KW: Record<string, string[]> = {
  customer: ["客户"], project: ["项目"], commitment: ["承诺"],
  source: ["信源", "渠道"], asset: ["内容资产", "内容"], supplier: ["供应商"],
};

function GraphView({
  docs, loading, onJump,
}: {
  docs: Record<string, { entities: OntoEntity[]; relations: string[] }>;
  loading: boolean;
  onJump: (id: string) => void;
}) {
  // 六类节点等距排布（八边形布局，中心为企业地图）
  const CX = 300, CY = 210, R = 150;
  const positions = (ONTO_TYPES as any).map((t: any, i: number) => {
    const a = (Math.PI * 2 * i) / 6 - Math.PI / 2;
    return { id: t.id, x: CX + R * Math.cos(a), y: CY + R * Math.sin(a) };
  });

  // 实体计数 + 该类型的实体名（取前 3）
  const counts: Record<string, number> = {};
  const samples: Record<string, string[]> = {};
  for (const t of ONTO_TYPES as any) {
    const ents = (docs[t.id]?.entities || []);
    counts[t.id] = ents.length;
    samples[t.id] = ents.map((e: OntoEntity) => e.name).slice(0, 3);
  }

  // 关系：扫描每类 relations，命中两类关键词 → 生成一条边
  const edges: { a: string; b: string; label: string }[] = [];
  for (const t of ONTO_TYPES as any) {
    for (const rel of (docs[t.id]?.relations || [])) {
      const hit: string[] = [];
      for (const other of ONTO_TYPES as any) {
        if (other.id === t.id) continue;
        if (TYPE_KW[other.id].some((k) => rel.includes(k))) hit.push(other.id);
      }
      for (const h of hit) {
        edges.push({ a: t.id, b: h, label: rel.replace(/\[\[|\]\]/g, "").slice(0, 18) });
      }
    }
  }
  // 去重（防止方向重复）
  const seen = new Set<string>();
  const uniqueEdges = edges.filter((e) => {
    const k = [e.a, e.b].sort().join("|");
    if (seen.has(k)) return false;
    seen.add(k); return true;
  });

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-2">
      {loading ? (
        <div className="py-16 text-center text-[11px] font-mono text-gray-600 flex items-center justify-center gap-2">
          <Loader2 size={12} className="animate-spin" /> 绘制企业地图…
        </div>
      ) : (
        <>
          <div className="text-[9px] font-mono text-gray-500 uppercase tracking-wider px-1">
            企业地图 · {counts.customer + counts.project + counts.commitment + counts.source + counts.asset + counts.supplier} 个实体 / {uniqueEdges.length} 条关系
          </div>
          <svg viewBox="0 0 600 420" className="w-full h-auto" style={{ maxHeight: 380 }}>
            <g>
              {/* 关系连线 */}
              {uniqueEdges.map((e, i) => {
                const p1 = positions.find((p: any) => p.id === e.a)!;
                const p2 = positions.find((p: any) => p.id === e.b)!;
                const mx = (p1.x + p2.x) / 2, my = (p1.y + p2.y) / 2;
                return (
                  <g key={i}>
                    <line x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y} stroke="#34d39955" strokeWidth={1} />
                    <text x={mx} y={my} textAnchor="middle" fill="#34d399aa" fontSize={7} fontFamily="monospace">{e.label}</text>
                  </g>
                );
              })}
              {/* 中心节点：企业地图 */}
              <circle cx={CX} cy={CY} r={26} fill="#0ea5e922" stroke="#0ea5e9" strokeWidth={1} />
              <text x={CX} y={CY + 3} textAnchor="middle" fill="#7dd3fc" fontSize={10} fontWeight={700}>企业地图</text>
              {/* 类型节点 */}
              {(ONTO_TYPES as any).map((t: any) => {
                const p = positions.find((q: any) => q.id === t.id)!;
                const count = counts[t.id] || 0;
                const r = 22 + Math.min(14, count * 2);
                const show = samples[t.id] || [];
                return (
                  <g key={t.id} onClick={() => onJump(t.id)} className="cursor-pointer" role="button">
                    <line x1={CX} y1={CY} x2={p.x} y2={p.y} stroke="#ffffff14" strokeWidth={1} />
                    <circle cx={p.x} cy={p.y} r={r} fill="#00000088" stroke={t.color} strokeWidth={1.2} />
                    <text x={p.x} y={p.y - 2} textAnchor="middle" fill={t.color} fontSize={8} fontWeight={700}>{t.label}</text>
                    <text x={p.x} y={p.y + 8} textAnchor="middle" fill="#9ca3af" fontSize={7} fontFamily="monospace">{count} 实体</text>
                    {show.map((s: string, i: number) => (
                      <text key={i} x={p.x} y={p.y + 20 + i * 9} textAnchor="middle" fill="#e5e7eb77" fontSize={6.5}>{s.slice(0, 8)}</text>
                    ))}
                  </g>
                );
              })}
            </g>
          </svg>
        </>
      )}
    </motion.div>
  );
}